import React from 'react';

// Below the server's 10s attention-lapse discard threshold. A trial that runs
// past it produces a response the server throws away, and a participant with
// fewer than five surviving correct trials scores null.
const DEFAULT_WINDOW_MS = 9000;
const VIEW = 100;

// Hole radius as a fraction of one grid cell, shared by the flat sheet, the
// folded stack and every option. One constant because the panels are drawn in a
// single coordinate space so they read as before/after: the two used to differ
// (0.28 against 0.26) with nothing to justify it, which is a visible difference
// between two figures that are supposed to depict the same punch.
const HOLE_R = 0.28;

const FOLD_LABEL = {
  'left-over-right': 'left over right',
  'right-over-left': 'right over left',
  'top-over-bottom': 'top over bottom',
  'bottom-over-top': 'bottom over top',
};

// Which half survives the fold, and which axis the crease runs along.
const FOLD_GEOMETRY = {
  'left-over-right': {
    axis: 'vertical',
    keep: 'right',
    arrow: { x1: 25, y1: 50, x2: 75, y2: 50, head: '67,44 75,50 67,56' },
  },
  'right-over-left': {
    axis: 'vertical',
    keep: 'left',
    arrow: { x1: 75, y1: 50, x2: 25, y2: 50, head: '33,44 25,50 33,56' },
  },
  'top-over-bottom': {
    axis: 'horizontal',
    keep: 'bottom',
    arrow: { x1: 50, y1: 25, x2: 50, y2: 75, head: '44,67 50,75 56,67' },
  },
  'bottom-over-top': {
    axis: 'horizontal',
    keep: 'top',
    arrow: { x1: 50, y1: 75, x2: 50, y2: 25, head: '44,33 50,25 56,33' },
  },
};

function cellRect(row, col, gridSize) {
  const step = VIEW / gridSize;
  return { x: col * step, y: row * step, step };
}

function GridSheet({ gridSize, holes = [], size, label, children }) {
  const step = VIEW / gridSize;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="img"
      aria-label={label}
      focusable="false"
    >
      <rect
        x="0"
        y="0"
        width={VIEW}
        height={VIEW}
        fill="var(--glass-bg-light)"
        stroke="var(--border-strong)"
        strokeWidth="2"
      />
      {Array.from({ length: gridSize - 1 }, (_, i) => (
        <g key={i}>
          <line
            x1={(i + 1) * step} y1="0" x2={(i + 1) * step} y2={VIEW}
            stroke="var(--border-subtle)" strokeWidth="1"
          />
          <line
            x1="0" y1={(i + 1) * step} x2={VIEW} y2={(i + 1) * step}
            stroke="var(--border-subtle)" strokeWidth="1"
          />
        </g>
      ))}
      {holes.map(([row, col], i) => {
        const { x, y } = cellRect(row, col, gridSize);
        return (
          <circle
            key={i}
            data-hole=""
            cx={x + step / 2}
            cy={y + step / 2}
            r={step * HOLE_R}
            fill="var(--text-primary)"
          />
        );
      })}
      {children}
    </svg>
  );
}

// The folded stack, drawn in the SAME coordinate space as the flat sheet so
// the two panels read as before/after rather than as two unrelated figures.
// Only the surviving half is paper; the folded-away half is left empty.
//
// `punch` is a cell of the full served grid that lies in the surviving half.
// It is drawn at its natural row and column with no offset arithmetic. Reading
// gridSize from the stimulus is load-bearing: the instrument has already moved
// from a 4×4 grid to an 8×8 grid.
function FoldedSheet({ stimulus, size, label }) {
  const { gridSize, fold, punch } = stimulus;
  const geom = FOLD_GEOMETRY[fold] || FOLD_GEOMETRY['left-over-right'];
  const vertical = geom.axis === 'vertical';
  const half = VIEW / 2;
  const step = VIEW / gridSize;
  // Guarded, like every other stimulus read in this battery. A bare destructure
  // of a missing `punch` throws DURING RENDER, which unmounts the runner
  // mid-block and loses every trial already recorded — a malformed single trial
  // would cost the whole block. Rendering the folded sheet with no hole is a
  // visibly wrong item the participant can skip; a thrown error is not
  // recoverable. Nothing else here needs the guard: `gridSize` degrades to NaN
  // geometry and `fold` already falls back above.
  const [pr, pc] = Array.isArray(punch) ? punch : [];

  const paper = vertical
    ? { x: geom.keep === 'right' ? half : 0, y: 0, width: half, height: VIEW }
    : { x: 0, y: geom.keep === 'bottom' ? half : 0, width: VIEW, height: half };

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="img"
      aria-label={label}
      focusable="false"
    >
      <rect
        {...paper}
        fill="var(--glass-bg-medium)"
        stroke="var(--border-strong)"
        strokeWidth="2"
      />
      {/* The crease, on the edge the other half came over. */}
      <line
        x1={vertical ? half : 0}
        y1={vertical ? 0 : half}
        x2={vertical ? half : VIEW}
        y2={vertical ? VIEW : half}
        stroke="var(--accent-primary)"
        strokeWidth="3"
      />
      {Number.isFinite(pr) && Number.isFinite(pc) && (
        <circle
          data-hole=""
          cx={pc * step + step / 2}
          cy={pr * step + step / 2}
          // The same radius the flat sheet and every option use. These panels
          // are drawn in one coordinate space so they read as before/after, and
          // a hole that changed size between them is a difference with no
          // meaning in the task.
          r={step * HOLE_R}
          fill="var(--text-primary)"
        />
      )}
    </svg>
  );
}

// Static two panels, never an animation: the server discards responses past
// 10s, so anything the participant has to wait through costs surviving trials.
function StimulusView({ stimulus, compact }) {
  const { gridSize, fold } = stimulus;
  const size = compact ? 42 : 130;
  const geom = FOLD_GEOMETRY[fold] || FOLD_GEOMETRY['left-over-right'];
  const vertical = geom.axis === 'vertical';

  return (
    <div style={{ display: 'grid', gap: compact ? 'var(--space-1)' : 'var(--space-3)', justifyItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 8 : 20 }}>
        <GridSheet gridSize={gridSize} size={size} label="Flat sheet">
          <line
            x1={vertical ? VIEW / 2 : 0}
            y1={vertical ? 0 : VIEW / 2}
            x2={vertical ? VIEW / 2 : VIEW}
            y2={vertical ? VIEW : VIEW / 2}
            stroke="var(--accent-primary)"
            strokeWidth="3"
            strokeDasharray="6 4"
          />
          <line
            data-fold-arrow=""
            x1={geom.arrow.x1}
            y1={geom.arrow.y1}
            x2={geom.arrow.x2}
            y2={geom.arrow.y2}
            stroke="var(--text-primary)"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <polygon
            data-fold-arrow-head=""
            points={geom.arrow.head}
            fill="var(--text-primary)"
          />
        </GridSheet>
        <span
          aria-hidden="true"
          style={{ color: 'var(--text-tertiary)', fontSize: compact ? 'var(--text-numeric-size)' : 'var(--text-section-size)' }}
        >
          →
        </span>
        <FoldedSheet stimulus={stimulus} size={size} label="Folded stack" />
      </div>
      {!compact && (
        <div style={{ fontSize: 'var(--text-numeric-size)', color: 'var(--text-secondary)', fontWeight: 600 }}>
          Fold {FOLD_LABEL[fold] || fold}, then punch
        </div>
      )}
    </div>
  );
}

export const paperFoldingTask = {
  id: 'paper-folding',
  name: 'Paper Folding',
  pillar: 'spatial',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'A square sheet is folded once, then a hole is punched through both layers.',
    'Pick the pattern of holes you would see when the sheet is unfolded.',
    'Answer on instinct — this one is timed, and slow answers do not count.',
  ],
  // Options come from the stimulus, so there is no static target set. The
  // fallback is empty rather than a placeholder: a wrong-shaped target array
  // would be submitted as a real choice.
  targets: [],

  // Deliberately empty. A worked example has to name a correct response, and
  // the correct unfold is exactly what the payload withholds — writing one by
  // hand would publish an answer key for a shape the server owns.
  examples: [],

  // The index is into `options` AS SERVED. The correct slot is uniformly
  // distributed across the four positions by design, so any client-side sort,
  // dedupe or reorder makes every graded index meaningless.
  toAnswer: (targetId) => ({ choice: Number(targetId) }),

  targetsFor: (trial) => {
    const options = trial?.stimulus?.options;
    if (!Array.isArray(options)) return [];
    const gridSize = trial.stimulus.gridSize;
    return options.map((holes, i) => ({
      id: String(i),
      label: `Option ${i + 1}`,
      keys: [String(i + 1)],
      hint: String(i + 1),
      node: (
        <GridSheet gridSize={gridSize} holes={holes} size={64} label={`Option ${i + 1}`} />
      ),
    }));
  },

  // fold_count, option_count and grid_size are served on params; grid_size is
  // read off the stimulus instead, which is the same value at the point of use.
  //
  // No isi_ms is served for this instrument, so the fixation and the gap are
  // fixed here rather than derived from the interval. Do NOT harmonise them
  // with flanker or stroop, which subtract their fixation from a served isi_ms.
  //
  // Number() is load-bearing: the runner's reaction-time ceiling is
  // windowMs + 250, so a window arriving as the string "9000" would
  // concatenate to "9000250" and accept every late response. || rather than ??
  // for the same reason a served 0 must fall back rather than time out
  // instantly.
  timing: (params) => ({
    fixationMs: 300,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: 400,
  }),

  // The correct option is not in the payload, and unfolding it client-side
  // would be reconstructing a withheld answer.
  feedbackAvailable: false,
  practiceExpectation: () => null,

  StimulusView,
};
